import { ArrowRight, Headphones, RefreshCw, Sparkles, Zap } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { track } from '../../lib/analytics/posthog'
import { startCheckoutSession } from '../../lib/start-checkout-session'
import { PLANS, getMinPlanForPaywall } from '../../lib/plans'
import { ShieldCheck } from 'lucide-react'

const SERIES_RESUME_MIN_PLAN = PLANS[getMinPlanForPaywall('series_resume')]

interface SeriesUpgradeBannerProps {
  reason: 'free-limit' | 'no-credits'
}

export function SeriesUpgradeBanner({ reason }: SeriesUpgradeBannerProps) {
  const navigate = useNavigate()

  if (reason === 'no-credits') {
    return (
      <div className="rounded-xl border border-warning-200 bg-warning-50/50 p-4">
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <p className="text-sm font-semibold text-warning-800">Your series is paused</p>
            <p className="mt-1 text-xs text-warning-700/80">
              You've used all your credits this month. Top up or wait for your next billing cycle.
            </p>
          </div>
          <button
            onClick={() => navigate('/billing')}
            className="flex shrink-0 cursor-pointer items-center gap-1 rounded-lg bg-warning-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-warning-700"
          >
            Top up
            <ArrowRight className="h-3 w-3" />
          </button>
        </div>
      </div>
    )
  }

  const handleQuickStart = async () => {
    track('paywall_banner_action', { location: 'series_upgrade_banner', action: 'quick_start_checkout' })
    await startCheckoutSession(SERIES_RESUME_MIN_PLAN.id)
  }

  const handleViewPlans = () => {
    track('paywall_banner_action', { location: 'series_upgrade_banner', action: 'view_plans' })
    navigate('/billing?from=series_free_limit')
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-brand-200 bg-gradient-to-br from-brand-50/80 via-white to-brand-50/40">
      <div className="px-6 py-8 sm:px-8 sm:py-10">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-100">
            <Sparkles className="h-6 w-6 text-brand-600" />
          </div>
          <h3 className="text-xl font-semibold text-foreground">
            Enjoyed your first episode?
          </h3>
          <p className="mx-auto mt-2 max-w-md text-sm text-foreground/60">
            Subscribe to keep your series running with fresh episodes delivered on your schedule, automatically.
          </p>
        </div>

        <div className="mx-auto mt-8 grid max-w-lg grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="flex items-center gap-3 rounded-xl bg-white/80 px-4 py-3 shadow-sm border border-black/5">
            <RefreshCw className="h-4 w-4 shrink-0 text-brand-600" />
            <span className="text-sm text-foreground/80">Episodes on autopilot</span>
          </div>
          <div className="flex items-center gap-3 rounded-xl bg-white/80 px-4 py-3 shadow-sm border border-black/5">
            <Headphones className="h-4 w-4 shrink-0 text-brand-600" />
            <span className="text-sm text-foreground/80">All voices & styles</span>
          </div>
          <div className="flex items-center gap-3 rounded-xl bg-white/80 px-4 py-3 shadow-sm border border-black/5">
            <Zap className="h-4 w-4 shrink-0 text-brand-600" />
            <span className="text-sm text-foreground/80">Priority generation</span>
          </div>
        </div>

        <div className="mx-auto mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
          <button
            onClick={handleQuickStart}
            className="flex cursor-pointer items-center gap-2 rounded-full bg-brand-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-brand-700 hover:shadow-md"
          >
            Continue your series
            <ArrowRight className="h-4 w-4" />
          </button>
          <button
            onClick={handleViewPlans}
            className="cursor-pointer text-sm font-medium text-foreground/50 transition-colors hover:text-foreground"
          >
            View all plans
          </button>
        </div>

        <div className="mt-4 flex items-center justify-center gap-1.5 text-[11px] text-foreground/40">
          <ShieldCheck className="h-3 w-3" />
          <span>From {SERIES_RESUME_MIN_PLAN.priceLabel}/mo · 7-day money-back guarantee</span>
        </div>
      </div>
    </div>
  )
}
